import { isAudioNode, isVideoNode } from '../../nodes/helpers';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
export const CREATIVE_UNMUTED_ICON = 'M16.3333 24.1792V22.1763C18.1741 21.6449 19.6714 20.6241 20.825 19.1138C21.9786 17.6037 22.5555 15.8894 22.5555 13.9708C22.5555 12.0523 21.9818 10.3346 20.8346 8.81797C19.6874 7.3013 18.187 6.28378 16.3333 5.76539V3.76251C18.7444 4.30696 20.7083 5.5271 22.225 7.42293C23.7417 9.31876 24.5 11.5014 24.5 13.9708C24.5 16.4403 23.7417 18.6229 22.225 20.5188C20.7083 22.4146 18.7444 23.6347 16.3333 24.1792ZM3.5 17.5V10.5H8.16667L14 4.66668V23.3333L8.16667 17.5H3.5ZM15.9445 18.8221V9.1388C16.9621 9.50183 17.7674 10.1306 18.3604 11.025C18.9535 11.9195 19.25 12.9111 19.25 14C19.25 15.0695 18.9503 16.0497 18.3508 16.9409C17.7511 17.8322 16.949 18.4593 15.9445 18.8221ZM12.0555 9.50835L9.02212 12.4446H5.44454V15.5555H9.02212L12.0555 18.5112V9.50835Z';
export const CREATIVE_MUTED_ICON = 'M23.5083 26.3667L19.7653 22.6237C19.2403 22.9865 18.6764 23.2992 18.0737 23.5617C17.4709 23.8242 16.8453 24.03 16.1971 24.1792V22.1763C16.586 22.0532 16.9636 21.9204 17.3299 21.7779C17.696 21.6352 18.0412 21.4504 18.3653 21.2237L13.8637 16.7029V23.3333L8.0304 17.5H3.36374V10.5H7.64161L1.49707 4.35547L2.87782 2.97501L24.8887 24.9667L23.5083 26.3667ZM22.7304 19.6L21.3304 18.2C21.6999 17.5583 21.9737 16.8815 22.152 16.1694C22.3303 15.4576 22.4195 14.7247 22.4195 13.9708C22.4195 12.0221 21.8523 10.2808 20.7179 8.7468C19.5837 7.21283 18.0768 6.21902 16.1971 5.76538V3.76251C18.6082 4.30695 20.5721 5.52709 22.0887 7.42292C23.6054 9.31876 24.3637 11.5014 24.3637 13.9708C24.3637 14.9755 24.2261 15.9543 23.9507 16.907C23.6752 17.8598 23.2684 18.7575 22.7304 19.6ZM18.8221 15.6917L16.1971 13.0667V9.27501C17.111 9.70278 17.8255 10.3445 18.3408 11.2C18.8561 12.0556 19.1137 12.9889 19.1137 14C19.1137 14.2917 19.0894 14.5785 19.0408 14.8604C18.9922 15.1424 18.9193 15.4195 18.8221 15.6917ZM13.8637 10.7333L10.8304 7.70001L13.8637 4.66667V10.7333ZM11.9195 18.6083V14.7779L9.58615 12.4445H5.30828V15.5555H8.86661L11.9195 18.6083Z';
export function isMediaDefaultMuted(creativeConfig) {
    return (creativeConfig.environment !== 5 /* AdEnvironment.VideoGenerator */ &&
        creativeConfig.environment !== 7 /* AdEnvironment.AudioGenerator */ &&
        creativeConfig.appView !== "dv" /* AppView.DesignView */);
}
export function shouldShowCreativeAudioToggle(creativeConfig) {
    const { environment } = creativeConfig;
    return (environment === 1 /* AdEnvironment.App */ ||
        environment === 0 /* AdEnvironment.LiveAd */ ||
        environment === 2 /* AdEnvironment.Preview */);
}
/**
 * Removes the shared creative audio toggle when no sound-capable element remains
 * or when every remaining media element failed to preload.
 */
export async function removeCreativeAudioToggleIfUnused(renderer) {
    var _a;
    const mediaElements = renderer.creativeDocument.elements.filter(element => isVideoNode(element) || isAudioNode(element));
    const preloadPromises = mediaElements.map(element => {
        const promise = renderer.preloadingElements.get(element.id);
        // Promises still pending after a media unload will never settle.
        // Race them against a resolved default
        return promise
            ? Promise.race([promise, Promise.resolve(false)]).catch(() => true)
            : Promise.resolve(false);
    });
    const preloadStatuses = await Promise.all(preloadPromises);
    const isAllRejected = preloadStatuses.every(Boolean);
    if (isAllRejected || mediaElements.length === 0) {
        (_a = renderer.audioToggle_m) === null || _a === void 0 ? void 0 : _a.remove();
        renderer.audioToggle_m = undefined;
    }
}
/**
 * Creative-wide mute button appended to the renderer root element.
 * The DOM element is a singleton shared through `renderer.audioToggle_m`;
 * each media renderer owns one instance of this class to coordinate with it.
 */
export class CreativeAudioToggle {
    constructor(_renderer, _creativeConfig) {
        this._renderer = _renderer;
        this._creativeConfig = _creativeConfig;
        this._onToggleClick = (event) => {
            event.stopPropagation();
            // State-free on purpose: this listener can outlive its creating instance
            // (it stays on the shared DOM element until detach), so it must not read
            // instance state. Renderer._muted is the source of truth; the icon updates
            // through the resulting 'mute' event.
            this._renderer.mute();
        };
        this._onRendererMute = (muted) => {
            this._muted = muted;
            this._setIcon(muted);
        };
        this._showToggleOnMouseOver = ({ type }) => {
            if (this._renderer.audioToggle_m) {
                this._renderer.audioToggle_m.style.visibility = type === 'mouseover' ? 'visible' : 'hidden';
            }
        };
        this._muted = isMediaDefaultMuted(_creativeConfig);
        this._renderer.on('mute', this._onRendererMute);
    }
    attach_m() {
        // toggle will exist already when node is rerendered
        if (!this._renderer.audioToggle_m) {
            this._renderer.audioToggle_m = this._createToggleElement();
        }
        this._renderer.rootElement.appendChild(this._renderer.audioToggle_m);
    }
    detach_m() {
        var _a, _b;
        this._removeMouseListeners();
        (_a = this._renderer.audioToggle_m) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', this._onToggleClick);
        (_b = this._renderer.audioToggle_m) === null || _b === void 0 ? void 0 : _b.remove();
        this._renderer.audioToggle_m = undefined;
        this._muted = isMediaDefaultMuted(this._creativeConfig);
    }
    setVisibilityOnPlayback_m(isPlaying) {
        const audioToggle = this._renderer.audioToggle_m;
        if (!audioToggle) {
            return;
        }
        audioToggle.style.visibility = isPlaying ? 'hidden' : 'visible';
        if (isPlaying) {
            this._renderer.rootElement.addEventListener('mouseout', this._showToggleOnMouseOver);
            this._renderer.rootElement.addEventListener('mouseover', this._showToggleOnMouseOver);
        }
        else {
            this._removeMouseListeners();
        }
    }
    destroy_m() {
        this._renderer.off('mute', this._onRendererMute);
        this._removeMouseListeners();
    }
    _createToggleElement() {
        const audioToggleElement = document.createElement('div');
        Object.assign(audioToggleElement.style, {
            borderRadius: '4px',
            width: '42px',
            height: '42px',
            background: 'black',
            opacity: '0.3',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'absolute',
            bottom: '4px',
            left: '4px'
        });
        const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
        const iconPath = document.createElementNS(SVG_NAMESPACE, 'path');
        svg.setAttribute('viewBox', '0 0 28 28');
        svg.setAttribute('width', '20');
        svg.setAttribute('height', '20');
        iconPath.setAttribute('d', this._muted ? CREATIVE_MUTED_ICON : CREATIVE_UNMUTED_ICON);
        iconPath.setAttribute('fill', 'white');
        svg.appendChild(iconPath);
        audioToggleElement.appendChild(svg);
        audioToggleElement.addEventListener('click', this._onToggleClick);
        return audioToggleElement;
    }
    _setIcon(muted) {
        var _a;
        const iconPath = (_a = this._renderer.audioToggle_m) === null || _a === void 0 ? void 0 : _a.querySelector('path');
        iconPath === null || iconPath === void 0 ? void 0 : iconPath.setAttribute('d', muted ? CREATIVE_MUTED_ICON : CREATIVE_UNMUTED_ICON);
    }
    _removeMouseListeners() {
        this._renderer.rootElement.removeEventListener('mouseover', this._showToggleOnMouseOver);
        this._renderer.rootElement.removeEventListener('mouseout', this._showToggleOnMouseOver);
    }
}
